/**
 * Phase 15.5 P1 — Beats Marketplace MVP
 * Phase 19.4 — Gift Producer button on every beat card.
 * Phase 19.8 — Background audio: play through the global PlayerProvider
 *               so the beat keeps playing while the user browses.
 * Phase 19.9 — Add to Playlist + Repost buttons.
 *
 * BeatCard — single beat tile for the Beats feed.
 * Shows: cover, producer photo + name, title, BPM, genre/mood, price + license,
 * featured ribbon, play button (global), Contact Producer CTA, gift, save, repost.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import { usePlayer } from "@/components/player/PlayerProvider";
import GiftPicker from "@/components/GiftPicker";
import AddBeatToPlaylistDialog from "@/components/AddBeatToPlaylistDialog";
import RecordInStudioButton from "@/components/studio/RecordInStudioButton";

const fmtPrice = (p) =>
  p == null || p === 0 ? "Free" : `$${Number(p).toFixed(2)}`;

const BeatCard = ({ beat, onContact, onReport, currentUserId, onHidden }) => {
  const navigate = useNavigate();
  const player = usePlayer();
  const [hasReported, setHasReported] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);
  const [reposted, setReposted] = useState(false);

  const beatTrackId = `beat_${beat.beat_id}`;
  const isCurrent = player?.track?.trackId === beatTrackId;
  const playing = !!isCurrent && player?.isPlaying;

  // Check whether the viewer has reposted this beat
  useEffect(() => {
    if (!currentUserId || !beat?.beat_id) return;
    let alive = true;
    api.get(`/beats/${beat.beat_id}/repost`)
      .then((r) => { if (alive) setReposted(!!r.data?.reposted); })
      .catch(() => {});
    return () => { alive = false; };
  }, [currentUserId, beat?.beat_id]);

  const togglePlay = () => {
    if (isCurrent) { player.toggle(); return; }
    player.play({
      trackId: beatTrackId,
      beat_id: beat.beat_id,
      title: beat.title,
      artist_name: beat.producer?.artist_name || beat.producer?.name || "Producer",
      artist_id: beat.producer?.user_id || beat.user_id,
      cover_url: beat.cover_url,
      audio_url: beat.audio_url,
      kind: "beat",
    }, { sourceLabel: "🎹 Beat Marketplace" });
  };

  const handleReport = async (e) => {
    e.stopPropagation();
    if (hasReported) return;
    setHasReported(true);
    try { await api.post(`/beats/${beat.beat_id}/report`, { reason: "user_report" }); }
    catch { /* surface in parent */ }
    onReport && onReport(beat);
  };

  const handleRepost = async (e) => {
    e?.stopPropagation?.();
    if (!currentUserId) { toast.error("Sign in to repost"); navigate("/login"); return; }
    try {
      const r = await api.post(`/beats/${beat.beat_id}/repost`);
      setReposted(!!r.data?.reposted);
      toast.success(r.data?.reposted ? "🔀 Added to RGM Shuffle" : "Repost removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not repost");
    }
  };

  const cover = getCoverUrl({ cover_url: beat.cover_url, cover_art: beat.cover_url });
  const producer = beat.producer || {};
  const producerName = producer.artist_name || producer.name || "Producer";
  const producerPhoto = getProfilePhotoUrl({
    profile_photo: producer.profile_photo,
    picture: producer.picture,
  });
  const isOwner = !!currentUserId && (currentUserId === beat.user_id
                                       || currentUserId === producer.user_id);

  const hideBeat = async () => {
    if (busy) return;
    if (!window.confirm(`Hide "${beat.title}" from the marketplace? You can restore it later from your dashboard.`)) return;
    setBusy(true);
    try {
      await api.delete(`/beats/${beat.beat_id}`);
      toast.success("Beat hidden from marketplace");
      onHidden?.(beat.beat_id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't hide beat");
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  };

  return (
    <>
    <Card
      className="relative overflow-hidden bg-white/[0.03] border-white/10 hover:border-pink-400/40 transition group"
      data-testid={`beat-card-${beat.beat_id}`}
    >
      {beat.featured && (
        <span
          className="absolute top-2 left-2 z-10 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 text-black"
          data-testid={`beat-featured-${beat.beat_id}`}
        >
          ⭐ Featured
        </span>
      )}

      <div className="aspect-square w-full bg-black/50 relative overflow-hidden">
        {cover && (
          <img
            src={cover}
            alt={beat.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
        <button
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Play"}
          data-testid={`beat-play-${beat.beat_id}`}
          className="absolute inset-0 flex items-center justify-center text-5xl text-white opacity-0 group-hover:opacity-100 transition bg-black/40"
        >
          {playing ? "❚❚" : "▶"}
        </button>
        {/* Phase 20.1 — Gift count visible on cover */}
        {(beat.gifts_received || 0) > 0 && (
          <span
            className="absolute bottom-2 right-2 z-[5] text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/80 text-amber-200 border border-amber-400/40"
            data-testid={`beat-gift-count-${beat.beat_id}`}
            title={`${beat.gifts_received} gift${beat.gifts_received === 1 ? '' : 's'} received`}
          >
            🎁 {beat.gifts_received}
          </span>
        )}
        {/* Phase 20.3 — Repost count chip */}
        {(beat.repost_count || 0) > 0 && (
          <span
            className="absolute bottom-2 left-2 z-[5] text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/80 text-purple-200 border border-purple-400/40"
            data-testid={`beat-repost-count-${beat.beat_id}`}
            title={`Reposted ${beat.repost_count} time${beat.repost_count === 1 ? '' : 's'}`}
          >
            🔁 {beat.repost_count}
          </span>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={producer.user_id ? `/artist/${producer.user_id}` : "#"}
            className="flex items-center gap-2 min-w-0 hover:underline"
            data-testid={`beat-producer-${beat.beat_id}`}
          >
            <img
              src={producerPhoto}
              alt=""
              className="w-7 h-7 rounded-full border border-white/15 flex-shrink-0 object-cover"
            />
            <div className="min-w-0">
              <div className="text-[11px] text-gray-400 truncate">{producerName}</div>
              <div className="text-sm font-bold text-white truncate" data-testid={`beat-title-${beat.beat_id}`}>
                {beat.title}
              </div>
            </div>
          </Link>
          <div className="text-right shrink-0">
            <div className="text-sm font-black text-pink-300" data-testid={`beat-price-${beat.beat_id}`}>
              {fmtPrice(beat.price)}
            </div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">
              {beat.license_type || "Lease"}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
          {beat.bpm != null && (
            <span className="px-1.5 py-0.5 rounded bg-purple-500/15 border border-purple-400/30 text-purple-200 font-bold"
                  data-testid={`beat-bpm-${beat.beat_id}`}>
              {beat.bpm} BPM
            </span>
          )}
          {beat.key && (
            <span className="px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 font-bold">
              {beat.key}
            </span>
          )}
          {beat.genre && (
            <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/30 text-amber-200">
              {beat.genre}
            </span>
          )}
          {beat.mood && (
            <span className="px-1.5 py-0.5 rounded bg-pink-500/15 border border-pink-400/30 text-pink-200">
              {beat.mood}
            </span>
          )}
        </div>

        {/* Action row 1 — Contact (primary CTA) */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            onClick={() => onContact && onContact(beat)}
            className="flex-1 h-8 text-xs bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold hover:opacity-90"
            data-testid={`beat-contact-${beat.beat_id}`}
          >
            ✉ Contact
          </Button>
          <RecordInStudioButton beat={beat} currentUser={currentUserId ? { user_id: currentUserId } : null} compact />
          {/* Phase 19.4 — Gift Producer on every beat card */}
          {!isOwner && (
            <GiftPicker
              targetType="beat"
              targetId={beat.beat_id}
              recipientId={beat.user_id || producer.user_id}
              currentUserId={currentUserId}
              compact
              className="shrink-0"
            />
          )}
          <button
            onClick={handleReport}
            disabled={hasReported}
            title={hasReported ? "Reported" : "Report this beat"}
            className="px-2 h-8 text-[11px] rounded-md bg-white/5 border border-white/10 text-gray-400 hover:bg-rose-500/10 hover:text-rose-300 disabled:opacity-40"
            data-testid={`beat-report-${beat.beat_id}`}
          >
            ⚑
          </button>
        </div>

        {/* Action row 2 — Repost + Add to Playlist (Phase 19.9) */}
        <div className="flex items-center gap-2 text-[11px]">
          <button
            onClick={handleRepost}
            className={`flex-1 h-7 rounded-md border transition-colors ${
              reposted
                ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-200"
                : "bg-white/5 border-white/10 text-gray-300 hover:border-purple-400/40 hover:text-white"
            }`}
            data-testid={`beat-repost-${beat.beat_id}`}
            title={reposted ? "Reposted" : "Repost to your profile"}
          >
            🔁 {reposted ? "Reposted" : "Repost"}
          </button>
          <button
            onClick={() => {
              if (!currentUserId) { navigate("/login"); return; }
              setShowAddToPlaylist(true);
            }}
            className="flex-1 h-7 rounded-md bg-white/5 border border-white/10 text-gray-300 hover:border-purple-400/40 hover:text-white transition-colors"
            data-testid={`beat-add-to-playlist-${beat.beat_id}`}
            title="Add to a beat playlist"
          >
            🎼 + Playlist
          </button>
        </div>
      </div>
    </Card>

    {/* Phase 19.10 — Add-to-playlist dialog */}
    <AddBeatToPlaylistDialog
      open={showAddToPlaylist}
      onClose={() => setShowAddToPlaylist(false)}
      beat={beat}
    />
    </>
  );
};

export default BeatCard;
